/**
 * Language Management API Types
 * 
 * Types for the language management endpoints
 * These match the actual API response structure from /api/admin/languages
 */

/**
 * Language Interface - Matches actual API response
 */
export interface Language {
  languageId: string;
  name: string;
  nameMm: string;
  nativeName: string;
  isoCode: string;
  description: string;
  iconUrl: string;
  colorCode: string;
  isActive: boolean;
  isRtl: boolean;
  sortOrder: number;
  contentCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Languages List Query Parameters
 */
export interface LanguagesQueryParams {
  page?: number;
  size?: number;
  search?: string;
  isActive?: boolean;
}

/**
 * Pagination metadata from API
 */
export interface LanguagesData {
  items: Language[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Create Language Request Payload
 */
export interface CreateLanguageRequest {
  name: string;
  nameMm: string;
  nativeName: string;
  isoCode: string;
  description: string;
  iconUrl?: string; // Optional - can use file upload instead
  colorCode: string;
  isActive: boolean;
  isRtl: boolean;
  sortOrder: number;
}

/**
 * Update Language Request Payload
 */
export interface UpdateLanguageRequest {
  name?: string;
  nameMm?: string;
  nativeName?: string;
  isoCode?: string;
  description?: string;
  iconUrl?: string;
  colorCode?: string;
  isActive?: boolean;
  isRtl?: boolean;
  sortOrder?: number;
}

/**
 * Create Language Response
 */
export interface CreateLanguageResponse {
  success: boolean;
  message: string;
  data: Language;
  timestamp: string;
}

/**
 * Update Language Response
 */
export interface UpdateLanguageResponse {
  success: boolean;
  message: string;
  data: Language;
  timestamp: string;
}

/**
 * Delete Language Response
 */
export interface DeleteLanguageResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

/**
 * Helper function to format date for display
 */
export const formatDateForDisplay = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Helper function to format datetime for display
 */
export const formatDateTimeForDisplay = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

